import React from 'react';

/**
 * Formats message text with WhatsApp-style and basic markdown markup.
 *
 * Supported:
 *   *bold*  or  **bold**
 *   _italic_
 *   ~strikethrough~  or  ~~strikethrough~~
 *   `inline code`
 *   ```code block```
 *   - / * unordered list items
 *   1. ordered list items
 *   https://… and www.… links, and [label](https://…) — open in a new tab
 */
export function FormattedMessage({ text }: { text: string }) {
  const segments: React.ReactNode[] = [];
  const codeBlockRe = /```([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = codeBlockRe.exec(text)) !== null) {
    // Text before the code block
    if (match.index > lastIndex) {
      segments.push(
        <span
          key={key++}
          dangerouslySetInnerHTML={{ __html: formatInline(text.slice(lastIndex, match.index)) }}
        />
      );
    }

    // Code block (content is NOT formatted, just escaped)
    const code = escapeHtml(match[1].replace(/^\n/, '').replace(/\n$/, ''));
    segments.push(
      <pre
        key={key++}
        className="my-1.5 overflow-x-auto rounded-lg bg-black/10 p-2.5 font-mono text-xs leading-relaxed"
      >
        <code dangerouslySetInnerHTML={{ __html: code }} />
      </pre>
    );

    lastIndex = match.index + match[0].length;
  }

  // Remaining text after last code block
  if (lastIndex < text.length) {
    segments.push(
      <span
        key={key++}
        dangerouslySetInnerHTML={{ __html: formatInline(text.slice(lastIndex)) }}
      />
    );
  }

  return <>{segments.length > 0 ? segments : text}</>;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Bare links: up to whitespace or a character that can't appear unescaped in one.
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<>"'`]+/gi;
const MARKDOWN_LINK_RE = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g;

/** Drop sentence punctuation after a link ("see https://x.com.") but keep a
 *  closing paren the link opened itself (Wikipedia-style URLs). */
function splitTrailing(url: string): [string, string] {
  let end = url.length;
  while (end > 0) {
    const ch = url.charAt(end - 1);
    if ('.,;:!?'.includes(ch)) {
      end--;
    } else if (ch === ')') {
      const body = url.slice(0, end);
      if ((body.match(/\(/g) || []).length >= (body.match(/\)/g) || []).length) break;
      end--;
    } else {
      break;
    }
  }
  return [url.slice(0, end), url.slice(end)];
}

function linkHtml(href: string, label: string): string {
  const url = /^https?:\/\//i.test(href) ? href : `https://${href}`;
  return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer" class="underline underline-offset-2 [overflow-wrap:anywhere] hover:opacity-80">${escapeHtml(label)}</a>`;
}

function formatInline(text: string): string {
  // Pull links out of the raw text before escaping and formatting, so the
  // _italic_ / *bold* rules can't mangle a URL like example.com/a_b_c.
  const links: string[] = [];
  const holdLink = (html: string) => {
    links.push(html);
    return `\uE000${links.length - 1}\uE000`;
  };
  text = text.replace(MARKDOWN_LINK_RE, (_, label, href) => holdLink(linkHtml(href, label)));
  text = text.replace(URL_RE, (match) => {
    const [url, trailing] = splitTrailing(match);
    return url ? holdLink(linkHtml(url, url)) + trailing : match;
  });

  let html = escapeHtml(text);

  // Protect inline code from other formatting by extracting it first
  const codes: string[] = [];
  html = html.replace(/`([^`\n]+)`/g, (_, code) => {
    codes.push(code);
    return `\x00CODE${codes.length - 1}\x00`;
  });

  // Bold: **text** first, then *text* (WhatsApp)
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<strong>$1</strong>');

  // Italic: _text_
  html = html.replace(/(?<!\w)_(.+?)_(?!\w)/g, '<em>$1</em>');

  // Strikethrough: ~~text~~ then ~text~ (WhatsApp)
  html = html.replace(/~~(.+?)~~/g, '<s>$1</s>');
  html = html.replace(/(?<!\w)~(.+?)~(?!\w)/g, '<s>$1</s>');

  // Restore inline code
  html = html.replace(/\x00CODE(\d+)\x00/g, (_, i) => {
    return `<code class="rounded bg-black/10 px-1 py-0.5 font-mono text-xs">${codes[parseInt(i)]}</code>`;
  });

  // Lists: lines starting with - or * or 1. (only at line start)
  html = html.replace(
    /^(\s*)([-*])\s+(.+)$/gm,
    '$1<span class="inline-flex gap-1.5"><span class="select-none text-gray-400">&bull;</span><span>$3</span></span>'
  );
  html = html.replace(
    /^(\s*)(\d+)\.\s+(.+)$/gm,
    '$1<span class="inline-flex gap-1.5"><span class="select-none text-gray-400">$2.</span><span>$3</span></span>'
  );

  html = html.replace(/\uE000(\d+)\uE000/g, (_, i) => links[parseInt(i)] ?? '');

  return html;
}
